import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useSupabase } from "@/auth/SupabaseProvider";

import { apiFetch } from "./client";
import type { AgentPromptRow, AgentPromptsResponse } from "./types";

/**
 * TanStack Query hook for the `/prompts` page. Lists every
 * `(agent_type, prompt_key)` known to the orchestrator's
 * `PROMPT_KEYS_FOR_AGENT_TYPE` allowlist, each with its active
 * version's metadata + body. Mirrors `useAgentConfig`'s posture:
 * single-fetch read, no auto-refresh on focus.
 *
 * Operators trigger a refresh by re-navigating or hitting reload; the
 * mutation hook below invalidates this cache on success so the
 * post-save refetch picks up the new version.
 *
 * Unseeded entries arrive with `version: 0` and empty `content` — the
 * page renders a "seed required" banner pointing at
 * `docs/agent-prompts.md`.
 */
export function useAgentPrompts() {
  const { client } = useSupabase();
  return useQuery({
    queryKey: ["agent-prompts"],
    queryFn: ({ signal }) =>
      apiFetch<AgentPromptsResponse>(client, "/api/admin/prompts", { signal }),
  });
}

/**
 * Single-prompt write mutation. The page calls this with the new
 * markdown content; the orchestrator creates a new monotonic version
 * row and returns the active row shape. Invalidates the list cache so
 * the next read pulls the new version + author + timestamp.
 *
 * Errors propagate to the caller verbatim; the page maps them onto a
 * banner. The hook does not retry.
 */
export function useUpdateAgentPrompt() {
  const { client } = useSupabase();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      agentType,
      promptKey,
      content,
    }: {
      agentType: string;
      promptKey: string;
      content: string;
    }) =>
      apiFetch<AgentPromptRow>(
        client,
        `/api/admin/prompts/${encodeURIComponent(agentType)}/${encodeURIComponent(promptKey)}`,
        { method: "PUT", body: { content } },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["agent-prompts"] });
    },
  });
}
